import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, Navigate } from 'react-router';

import { HowDiagram } from './HowDiagram';
import styles from './how.module.css';
import { ClockIcon, CloseIcon, LockIcon, PlugIcon, ShieldCheckIcon } from './Icons';
import { SOURCE_URL } from './sourceUrl';
import { isApp } from '@/lib/app';
import { useT, type MessageKey } from '@/i18n';

/**
 * “How Bridge works”: one window with the picture of what each side sees and
 * why nobody in between can read the secret. It opens from the guide on the
 * first screen (“Why it’s safe”) and from the old /about address.
 */

interface HowContextValue {
  readonly open: () => void;
}

const HowContext = createContext<HowContextValue>({ open: () => undefined });

function useHowBridgeWorks(): HowContextValue {
  return useContext(HowContext);
}

const POINTS: readonly { readonly icon: ReactNode; readonly title: MessageKey; readonly text: MessageKey }[] = [
  { icon: <LockIcon size={18} />, title: 'howBridgeWorks.encryptedTitle', text: 'howBridgeWorks.encryptedText' },
  { icon: <ShieldCheckIcon size={18} />, title: 'howBridgeWorks.unreadableTitle', text: 'howBridgeWorks.unreadableText' },
  { icon: <ClockIcon size={18} />, title: 'howBridgeWorks.goneTitle', text: 'howBridgeWorks.goneText' },
];

function HowDialog({ onClose }: { readonly onClose: () => void }): React.JSX.Element {
  const t = useT();
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Focus moves into the window, and back to whatever opened it when it closes.
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      opener?.focus();
    };
  }, [onClose]);

  return (
    <div
      className={styles.backdrop}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div role="dialog" aria-modal="true" aria-labelledby="how-title" aria-describedby="how-lead" className={styles.dialog} data-testid="how-dialog">
        <button ref={closeRef} type="button" className={styles.close} onClick={onClose} aria-label={t('common.close')}>
          <CloseIcon size={18} />
        </button>
        <div className={styles.top}>
        <p className={styles.eyebrow}>{t('howBridgeWorks.eyebrow')}</p>
        <h2 id="how-title" className={styles.title}>
          {t('howBridgeWorks.title')}
        </h2>
        <p id="how-lead" className={styles.lead}>
          {t('howBridgeWorks.lead')}
        </p>
        </div>
        <div className={styles.scroll} tabIndex={0}>
        <HowDiagram />
        <ul className={styles.points}>
          {POINTS.map((point) => (
            <li key={point.title} className={styles.point}>
              <span className={styles.pointIcon} aria-hidden="true">
                {point.icon}
              </span>
              <span className={styles.pointTitle}>{t(point.title)}</span>
              <span className={styles.pointText}>{t(point.text)}</span>
            </li>
          ))}
        </ul>
        <p className={styles.note}>
          <span className={styles.noteIcon} aria-hidden="true">
            <PlugIcon size={16} />
          </span>
          <span>
            {t('howBridgeWorks.note')}
          </span>
        </p>
        </div>
        <p className={styles.foot}>
          <span>{t('howBridgeWorks.license')}</span>
          {/* the app is clean: no links to the source or the site's pages */}
          {isApp() ? null : (
            <>
              <a href={SOURCE_URL} target="_blank" rel="noreferrer">
                {t('howBridgeWorks.source')}
              </a>
              <Link to="/developers" onClick={onClose}>
                {t('howBridgeWorks.api')}
              </Link>
            </>
          )}
        </p>
      </div>
    </div>
  );
}

export function HowProvider({ children }: { readonly children: ReactNode }): React.JSX.Element {
  const [shown, setShown] = useState(false);
  const open = useCallback(() => {
    setShown(true);
  }, []);
  const close = useCallback(() => {
    setShown(false);
  }, []);
  const value = useMemo(() => ({ open }), [open]);
  return (
    <HowContext.Provider value={value}>
      {children}
      {shown ? <HowDialog onClose={close} /> : null}
    </HowContext.Provider>
  );
}

/** “How it works” as a link-looking button: it opens the window over the page you are on. */
export function HowLink({ className, children, testId }: { readonly className?: string | undefined; readonly children?: ReactNode; readonly testId?: string }): React.JSX.Element {
  const t = useT();
  const { open } = useHowBridgeWorks();
  return (
    <button type="button" className={className} onClick={open} aria-haspopup="dialog" data-testid={testId}>
      {children ?? t('howBridgeWorks.link')}
    </button>
  );
}

/** The old address of the How it works page: the app, with the window open. */
export function HowRedirect(): React.JSX.Element {
  const { open } = useHowBridgeWorks();
  useEffect(() => {
    open();
  }, [open]);
  return <Navigate to="/" replace />;
}
